import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { logoutClient } from './api';
import CustomAlert from './CustomAlert';
import HamburgerMenu from './HamburgerMenu';

const getStyles = (theme) => {
    const isDark = theme === 'dark';
    const colors = {
        background: isDark ? '#1e1e1e' : '#fff',
        text: isDark ? '#e0e0e0' : '#212529',
        border: isDark ? '#444' : '#ccc',
        link: isDark ? '#6cb2f7' : '#007bff',
        buttonBg: isDark ? '#6cb2f7' : '#007bff',
        buttonText: isDark ? '#121212' : '#fff',
        menuItemBg: isDark ? '#333' : '#fff',
        menuItemColor: isDark ? '#e0e0e0' : '#333',
        cardBg: isDark ? '#2a2a2a' : '#f8f9fa',
        inputBg: isDark ? '#333' : '#fff',
        inputBorder: isDark ? '#555' : '#ccc',
        inputText: isDark ? '#e0e0e0' : '#212529',
        success: isDark ? '#4caf50' : '#28a745',
        warning: isDark ? '#ff9800' : '#ffc107',
        danger: isDark ? '#f44336' : '#dc3545',
        info: isDark ? '#2196f3' : '#17a2b8',
    };
    return {
        page: { backgroundColor: colors.background, minHeight: '100vh', padding: '20px' },
        container: { maxWidth: '1200px', margin: '0 auto', fontFamily: 'sans-serif' },
        pageHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
        pageTitle: { fontSize: '24px', margin: 0 },
        menuItem: {
            padding: '10px 15px',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            display: 'block',
            textDecoration: 'none',
            color: colors.menuItemColor,
        },
        menuItemButton: {
            background: 'none',
            border: 'none',
            padding: '10px 15px',
            width: '100%',
            textAlign: 'left',
            cursor: 'pointer',
            fontSize: '16px',
            fontFamily: 'sans-serif',
            color: colors.menuItemColor,
        },
        card: { backgroundColor: colors.cardBg, border: `1px solid ${colors.border}`, borderRadius: '8px', padding: '20px', boxShadow: `0 2px 5px ${colors.border}`, marginBottom: '20px' },
        formGroup: { marginBottom: '20px' },
        label: { display: 'block', marginBottom: '5px', fontWeight: 'bold', color: colors.text },
        select: { 
            width: '100%', 
            padding: '10px', 
            border: `1px solid ${colors.inputBorder}`, 
            borderRadius: '4px', 
            backgroundColor: colors.inputBg, 
            color: colors.inputText,
            fontSize: '16px'
        },
        fileInputContainer: { 
            marginBottom: '20px', 
            border: `2px dashed ${colors.border}`, 
            padding: '30px', 
            borderRadius: '8px', 
            textAlign: 'center',
            backgroundColor: colors.cardBg
        },
        fileInput: { 
            marginBottom: '10px',
            padding: '10px',
            border: `1px solid ${colors.inputBorder}`,
            borderRadius: '4px',
            backgroundColor: colors.inputBg,
            color: colors.inputText,
            width: '100%'
        },
        button: { 
            padding: '12px 24px', 
            backgroundColor: colors.buttonBg, 
            color: colors.buttonText, 
            border: 'none', 
            borderRadius: '4px', 
            cursor: 'pointer', 
            fontSize: '16px', 
            fontWeight: 'bold',
            marginRight: '10px'
        },
        buttonSecondary: {
            padding: '12px 24px',
            backgroundColor: colors.info,
            color: colors.buttonText,
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: 'bold'
        },
        instructions: { 
            fontSize: '14px', 
            color: colors.text, 
            marginBottom: '20px', 
            textAlign: 'left',
            backgroundColor: colors.cardBg,
            padding: '15px',
            borderRadius: '4px',
            border: `1px solid ${colors.border}`
        },
        list: { paddingLeft: '20px' },
        siteInfo: {
            backgroundColor: colors.cardBg,
            padding: '15px',
            borderRadius: '4px',
            border: `1px solid ${colors.border}`,
            marginBottom: '20px'
        },
        inspectionInfo: {
            backgroundColor: colors.cardBg,
            padding: '15px',
            borderRadius: '4px',
            border: `1px solid ${colors.border}`,
            marginBottom: '20px'
        }
    };
};

function UploadCheckList({ theme }) {
    const styles = getStyles(theme);
    const [selectedFile, setSelectedFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [alert, setAlert] = useState({ show: false, message: '', type: '' });
    const [sites, setSites] = useState([]);
    const [inspections, setInspections] = useState([]);
    const [selectedSite, setSelectedSite] = useState('');
    const [selectedInspection, setSelectedInspection] = useState('');
    const navigate = useNavigate();

    // Fetch sites and checklist types on component mount
    useEffect(() => {
        const fetchData = async () => {
            try {
                const [sitesRes, checklistTypesRes] = await Promise.all([
                    api.get('/sites/'),
                    api.get('/checklist-types/')
                ]);
                
                const sitesData = Array.isArray(sitesRes.data) ? sitesRes.data : (sitesRes.data.results || []);
                const checklistTypesData = Array.isArray(checklistTypesRes.data) ? checklistTypesRes.data : (checklistTypesRes.data.results || []);
                
                setSites(sitesData);
                setInspections(checklistTypesData);
            } catch (error) {
                console.error('Error fetching data:', error);
                setAlert({ show: true, message: 'Failed to load sites and checklist types', type: 'error' });
            }
        };

        fetchData();
    }, []);

    const handleFileChange = (event) => {
        setSelectedFile(event.target.files[0]);
    };

    const handleSiteChange = (event) => {
        setSelectedSite(event.target.value);
        setSelectedInspection(''); // Reset inspection when site changes
    };

    const handleInspectionChange = (event) => {
        setSelectedInspection(event.target.value);
    };

    const handleUpload = () => {
        if (!selectedFile) {
            setAlert({ show: true, message: 'Please select a file first.', type: 'error' });
            return;
        }
        
        if (!selectedSite) {
            setAlert({ show: true, message: 'Please select a site first.', type: 'error' });
            return;
        }

        setLoading(true);
        const formData = new FormData();
        formData.append('file', selectedFile);
        formData.append('site_id', selectedSite);

        console.log('Uploading checklist with site_id:', selectedSite);
        console.log('File:', selectedFile);

        api.post('/upload-checklist/', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        })
        .then(response => {
            setAlert({ show: true, message: response.data.message, type: 'success' });
            // Clear form after successful upload
            setSelectedFile(null);
            setSelectedSite('');
            setSelectedInspection('');
            const fileInput = document.getElementById('checklist-file-input');
            if(fileInput) fileInput.value = null;
        })
        .catch(error => {
            setAlert({ show: true, message: error.response?.data?.error || 'An unknown error occurred.', type: 'error' });
        })
        .finally(() => {
            setLoading(false);
        });
    };

    const handleBack = () => {
        navigate('/checklist');
    };

    const handleLogout = () => {
        logoutClient();
        navigate('/login');
    };


    return (
        <div style={styles.page}>
            <HamburgerMenu theme={theme} onLogout={handleLogout} />
            <div style={styles.container}>
                <div style={styles.pageHeader}>
                    <h1 style={styles.pageTitle}>Upload Checklist</h1>
                </div>

                {alert.show && (
                    <CustomAlert
                        message={alert.message}
                        type={alert.type}
                        onClose={() => setAlert({ show: false, message: '', type: '' })}
                    />
                )}

                <div style={styles.card}>
                    <h2 style={{ marginTop: 0, marginBottom: '20px' }}>Site and Checklist Type Selection</h2>
                    
                    <div style={styles.formGroup}>
                        <label style={styles.label} htmlFor="site-select">Select Site:</label>
                        <select 
                            id="site-select" 
                            style={styles.select} 
                            value={selectedSite} 
                            onChange={handleSiteChange}
                        >
                            <option value="">Choose a site...</option>
                            {sites.map(site => (
                                <option key={site.id} value={site.id}>
                                    {site.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    {selectedSite && (
                        <div style={styles.formGroup}>
                            <label style={styles.label} htmlFor="checklist-type-select">Select Checklist Type (Optional):</label>
                            <select 
                                id="checklist-type-select" 
                                style={styles.select} 
                                value={selectedInspection} 
                                onChange={handleInspectionChange}
                            >
                                <option value="">Choose a checklist type...</option>
                                {inspections.map(checklistType => (
                                    <option key={checklistType.id} value={checklistType.id}>
                                        {checklistType.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {selectedSite && selectedInspection && (
                        <div style={styles.siteInfo}>
                            <h3>Selected Site: {sites.find(s => s.id === parseInt(selectedSite))?.name}</h3>
                            <h3>Selected Checklist Type: {inspections.find(ct => ct.id === parseInt(selectedInspection))?.name}</h3>
                        </div>
                    )}
                </div>

                <div style={styles.card}>
                    <h2 style={{ marginTop: 0, marginBottom: '20px' }}>Upload Checklist File</h2>
                    
                    <div style={styles.instructions}>
                        <h3>Instructions:</h3>
                        <ul style={styles.list}>
                            <li>Select a site from the dropdown above</li>
                            <li>Optionally select a checklist type (or leave blank to create new)</li>
                            <li>Choose a checklist file to upload (Excel format .xlsx or .xls)</li>
                            <li>The checklist type will be determined from the file content</li>
                            <li>Make sure the checklist format matches the expected template</li>
                            <li><strong>Supported formats:</strong></li>
                            <li style={{marginLeft: '20px'}}>• <strong>New format (5 columns):</strong> Checklist Name, Card, Steps, Work Center, Work Area</li>
                            <li style={{marginLeft: '20px'}}>• <strong>Legacy format (6 columns):</strong> Checklist Name, Order ID, Card, Steps, Work Center, Work Area</li>
                            <li style={{marginLeft: '20px'}}>  <em>Note: Order ID column is ignored in legacy format</em></li>
                            <li>Note: Site information will be applied based on your site selection</li>
                        </ul>
                    </div>

                    <div style={styles.fileInputContainer}>
                        <input
                            id="checklist-file-input"
                            type="file"
                            accept=".xlsx,.xls"
                            onChange={handleFileChange}
                            style={styles.fileInput}
                        />
                        {selectedFile && (
                            <p style={{ margin: '10px 0', color: styles.text }}>
                                Selected file: {selectedFile.name}
                            </p>
                        )}
                    </div>

                    <div style={{ marginTop: '20px' }}>
                        <button 
                            style={styles.button} 
                            onClick={handleUpload}
                            disabled={loading || !selectedFile || !selectedSite}
                        >
                            {loading ? 'Uploading...' : 'Upload Checklist'}
                        </button>
                        <button 
                            style={styles.buttonSecondary} 
                            onClick={handleBack}
                        >
                            Back to Checklist
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default UploadCheckList;
